import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from '@atlassian/jira-controllers-global-theme';
import { useFlagsService } from '@atlassian/jira-flags';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { CUSTOM_THEME_ADMIN_FLAG_ID } from './common/constants';
import { setAdminFlagDismissed, fetchAdminFlagDismissed } from './services';
import CustomThemeAdminFlag from './ui';
/**
 * Ensures the visibility of a custom theme notification for site administrators. This involves determining
 * the appropriateness of displaying a notification based on the admin's status, theme customization, and
 * whether the notification has previously been dismissed.
 */
const useCustomThemeAdminFlag = () => {
	const { showFlag, dismissFlag, hasFlag } = useFlagsService();
	const tenantContext = useTenantContext();
	const [isFlagDismissed, setIsFlagDismissed] = useState<boolean | undefined>(undefined);
	const [{ navigationTheme }] = useTheme();
	const handleDismiss = useCallback(() => {
		if (hasFlag?.(CUSTOM_THEME_ADMIN_FLAG_ID)) {
			dismissFlag(CUSTOM_THEME_ADMIN_FLAG_ID);
			setIsFlagDismissed(true);
			setAdminFlagDismissed(true);
		}
	}, [dismissFlag, hasFlag]);
	const handleShowFlag = useCallback(() => {
		showFlag({
			id: CUSTOM_THEME_ADMIN_FLAG_ID,
			render: (props: FlagCustomRenderProps) => (
				<CustomThemeAdminFlag
					{...props}
					onDismiss={handleDismiss}
					analyticsAttributes={{
						backgroundColor: navigationTheme.backgroundColor,
						highlightColor: navigationTheme.highlightColor,
					}}
				/>
			),
		});
	}, [handleDismiss, showFlag, navigationTheme]);
	useEffect(() => {
		if (
			tenantContext.isSiteAdmin &&
			navigationTheme.usesCustomColours &&
			isFlagDismissed === undefined &&
			!hasFlag?.(CUSTOM_THEME_ADMIN_FLAG_ID)
		) {
			fetchAdminFlagDismissed().then((result) => {
				setIsFlagDismissed(result || false);
			});
		}
	}, [tenantContext, navigationTheme, isFlagDismissed, hasFlag]);
	useEffect(() => {
		if (isFlagDismissed === false) {
			handleShowFlag();
		}
	}, [handleShowFlag, isFlagDismissed]);
	return null;
};
export default useCustomThemeAdminFlag;
